import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Zap, RotateCw, BookOpenCheck, Trophy, AlertCircle } from 'lucide-react';
import { Button, FeedbackPopup, computeXpReward, type XpReward } from '@jeraisonne/ui';
import type { WarmupQuestion } from '@jeraisonne/db';
import { MarkdownMath } from '@jeraisonne/ui';
import { ChoiceButton } from '@/components/exercise/choice-button';
import { PaperChoices, PaperColumn, PaperPage, usePaperPlayerForMe } from '@/features/paper-player';
import { freeSheetKey } from '@/features/scratch-sheet/use-free-sheet';
import { QuestionVisuals } from '@/components/exercise/question-visuals';
import { supabase } from '@/lib/supabase';
import {
  generateWarmup,
  submitWarmupAnswer,
  finishWarmup,
  warmupErrorMessage,
  WarmupError,
  type WarmupSession,
  type WarmupAnswerResult,
  type WarmupSummary,
} from '@/lib/warmup/warmup-client';
import { Spinner } from '@/components/spinner';

type Phase = 'loading' | 'playing' | 'finishing' | 'summary' | 'error';

export function StudentWarmupPage() {
  const navigate = useNavigate();

  const [phase, setPhase] = useState<Phase>('loading');
  const [session, setSession] = useState<WarmupSession | null>(null);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [selectedChoiceId, setSelectedChoiceId] = useState<string | null>(null);
  // Exercice « sur la feuille de brouillon » (drapeau `paper_player`).
  const paper = usePaperPlayerForMe();
  const [answerResult, setAnswerResult] = useState<WarmupAnswerResult | null>(null);
  const [summary, setSummary] = useState<WarmupSummary | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // ─── Gamification (popup XP + bonus de série) ────────────────────────────
  // Série de bonnes réponses d'affilée + XP cumulé, affichés dans une popup
  // centrée après chaque réponse — même UX que le player V2. L'XP réelle est
  // créditée côté serveur par `finishWarmup`.
  const [streak, setStreak] = useState(0);
  const [sessionXp, setSessionXp] = useState(0);
  const [reward, setReward] = useState<{
    value: XpReward;
    wasCorrect: boolean;
    explanationMd?: string;
    errorFeedbackMd?: string;
  } | null>(null);

  const stepStartedAt = useRef<number>(Date.now());

  const start = useCallback(async () => {
    setPhase('loading');
    setSession(null);
    setSummary(null);
    setCurrentIdx(0);
    setSelectedChoiceId(null);
    setAnswerResult(null);
    setErrorMsg(null);
    setStreak(0);
    setSessionXp(0);
    setReward(null);
    try {
      const s = await generateWarmup(10);
      setSession(s);
      setPhase('playing');
      stepStartedAt.current = Date.now();
    } catch (err) {
      setErrorMsg(
        err instanceof WarmupError ? warmupErrorMessage(err.code) : 'Erreur inattendue. Réessaie.',
      );
      setPhase('error');
    }
  }, []);

  useEffect(() => {
    void start();
  }, [start]);

  const question: WarmupQuestion | undefined = session?.questions[currentIdx];
  const total = session?.questions.length ?? 0;

  async function handleSelect(choiceId: string) {
    if (!session || !question || answerResult || submitting) return;
    setSelectedChoiceId(choiceId);
    setSubmitting(true);
    try {
      const result = await submitWarmupAnswer({
        attemptId: session.attemptId,
        stepId: question.step_id,
        choiceId,
        timeMs: Date.now() - stepStartedAt.current,
      });
      setAnswerResult(result);
      // Récompense gamifiée : XP (base + bonus de série) → popup centrée.
      const xp = computeXpReward(result.wasCorrect, streak);
      setStreak(xp.streak);
      setSessionXp((total) => total + xp.total);
      setReward({
        value: xp,
        wasCorrect: result.wasCorrect,
        explanationMd: result.explanationMd,
        errorFeedbackMd: result.feedbackMd ?? undefined,
      });
    } catch (err) {
      setErrorMsg(
        err instanceof WarmupError ? warmupErrorMessage(err.code) : 'Erreur inattendue. Réessaie.',
      );
      setPhase('error');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleNext() {
    if (!session) return;
    if (currentIdx < total - 1) {
      setCurrentIdx((i) => i + 1);
      setSelectedChoiceId(null);
      setAnswerResult(null);
      stepStartedAt.current = Date.now();
      return;
    }
    // Dernière question → finalise la session.
    setPhase('finishing');
    try {
      const s = await finishWarmup(session.attemptId);
      setSummary(s);
      setPhase('summary');
    } catch (err) {
      setErrorMsg(
        err instanceof WarmupError ? warmupErrorMessage(err.code) : 'Erreur inattendue. Réessaie.',
      );
      setPhase('error');
    }
  }

  // ── Rendu ─────────────────────────────────────────────────────────────────

  if (phase === 'loading' || phase === 'finishing') {
    return (
      <Shell>
        <div className="space-y-3">
          <div role="status" className="text-ink-400 flex items-center gap-2 text-sm">
            <Spinner className="text-accent-400 h-4 w-4" />
            {phase === 'finishing' ? 'Calcul de ton score…' : 'Préparation de ton échauffement…'}
          </div>
          <div className="bg-ink-100 h-6 w-40 animate-pulse rounded" />
          <div className="bg-ink-100 h-32 animate-pulse rounded-2xl" />
          <div className="bg-ink-100 h-12 animate-pulse rounded-xl" />
        </div>
      </Shell>
    );
  }

  if (phase === 'error') {
    return (
      <Shell>
        <div className="bg-error-50 border-error-100 flex items-start gap-2 rounded-xl border px-4 py-3">
          <AlertCircle className="text-error-600 mt-0.5 h-4 w-4 shrink-0" />
          <p className="text-error-700 font-sans text-sm">{errorMsg}</p>
        </div>
        <div className="mt-4 flex flex-col gap-2">
          <Button onClick={() => void start()} fullWidth>
            Réessayer
          </Button>
          <Button variant="outline" fullWidth onClick={() => navigate({ to: '/student/revise' })}>
            Retour à l’entraînement
          </Button>
        </div>
      </Shell>
    );
  }

  if (phase === 'summary' && summary) {
    return (
      <Shell>
        <WarmupSummaryScreen
          summary={summary}
          accessStatus={session?.accessStatus ?? 'FREE'}
          onContinue={() => void start()}
          onReviseGuided={() => navigate({ to: '/student/revise' })}
        />
      </Shell>
    );
  }

  if (phase === 'playing' && question) {
    const revealed = answerResult !== null;
    return (
      <Shell progress={{ current: currentIdx + 1, total }}>
        {paper ? (
          <PaperPage className="-mx-4 min-h-0 sm:rounded-2xl">
            <PaperColumn className="pt-3">
              <div className="text-ink-900 mb-3 font-sans text-base">
                <MarkdownMath>{question.prompt_md}</MarkdownMath>
              </div>
              <div className="pp-figure">
                <QuestionVisuals visuals={question.visuals} />
              </div>
              <PaperChoices
                questionKey={question.step_id}
                promptMd={question.prompt_md}
                choices={question.choices}
                selected={selectedChoiceId}
                revealed={revealed}
                busy={submitting}
                onChoose={(id) => void handleSelect(id)}
                storageKey={freeSheetKey('warmup', session?.attemptId, question.step_id)}
              />
            </PaperColumn>
          </PaperPage>
        ) : (
          <>
            {/* Question */}
            <div className="text-ink-900 mb-4 font-sans text-base">
              <MarkdownMath>{question.prompt_md}</MarkdownMath>
            </div>
            <QuestionVisuals visuals={question.visuals} />

            {/* Choix */}
            <div className="space-y-2">
              {question.choices.map((choice, i) => (
                <ChoiceButton
                  key={choice.id}
                  choice={choice}
                  index={i}
                  selected={selectedChoiceId === choice.id}
                  revealed={revealed}
                  onClick={() => void handleSelect(choice.id)}
                />
              ))}
            </div>
          </>
        )}

        {/* Feedback gamifié : popup centrée. Un tap n'importe où la ferme ET
            passe à la question suivante (ou au score sur la dernière). */}
        {reward && (
          <FeedbackPopup
            reward={reward.value}
            wasCorrect={reward.wasCorrect}
            sessionXp={sessionXp}
            explanationMd={reward.explanationMd}
            errorFeedbackMd={reward.errorFeedbackMd}
            onClose={() => {
              setReward(null);
              void handleNext();
            }}
          />
        )}
      </Shell>
    );
  }

  return null;
}

// ─── Layout ───────────────────────────────────────────────────────────────────

function Shell({
  children,
  progress,
}: {
  children: React.ReactNode;
  progress?: { current: number; total: number };
}) {
  const navigate = useNavigate();

  const { data: warmupIllustration } = useQuery({
    queryKey: ['warmup-template-illustration'],
    staleTime: 60 * 60 * 1000,
    queryFn: async () => {
      const { data } = await supabase
        .from('objective_templates')
        .select('illustration_url')
        .eq('type', 'warmup')
        .order('display_order', { ascending: true })
        .limit(1)
        .maybeSingle();
      return (data as { illustration_url: string | null } | null)?.illustration_url ?? null;
    },
  });

  return (
    <div className="mx-auto max-w-lg pb-24 lg:max-w-3xl">
      <div className="from-accent-50 bg-linear-to-br to-ink-50 relative aspect-video w-full overflow-hidden sm:mt-4 sm:rounded-2xl">
        {warmupIllustration && (
          <img
            src={warmupIllustration}
            alt=""
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 h-full w-full object-cover"
          />
        )}
        <div
          aria-hidden="true"
          className="bg-linear-to-t from-ink-50 pointer-events-none absolute inset-x-0 bottom-0 h-1/3 to-transparent"
        />
        <button
          type="button"
          onClick={() => navigate({ to: '/student/revise' })}
          aria-label="Retour à l’entraînement"
          className="absolute left-3 top-3 flex h-11 w-11 items-center justify-center rounded-full bg-[#ffffff]/90 text-[#0d0b06] shadow-md backdrop-blur-sm transition-transform active:scale-95"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        {progress && (
          <div className="absolute inset-x-0 bottom-0 px-4 pb-3">
            <div className="text-ink-700 mb-1.5 flex items-center justify-between text-xs font-semibold">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white/90 px-2 py-0.5 shadow-sm backdrop-blur-sm">
                <Zap className="text-accent-500 h-3.5 w-3.5" /> Warmup
              </span>
              <span className="rounded-full bg-white/90 px-2 py-0.5 shadow-sm backdrop-blur-sm">
                {progress.current} / {progress.total}
              </span>
            </div>
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/70 shadow-sm backdrop-blur-sm">
              <div
                className="bg-accent-400 h-full rounded-full transition-all"
                style={{ width: `${(progress.current / progress.total) * 100}%` }}
              />
            </div>
          </div>
        )}
      </div>
      <div className="px-4 py-6 sm:py-8">{children}</div>
    </div>
  );
}

// ─── Écran de score ───────────────────────────────────────────────────────────

function WarmupSummaryScreen({
  summary,
  accessStatus,
  onContinue,
  onReviseGuided,
}: {
  summary: WarmupSummary;
  accessStatus: 'FREE' | 'PREMIUM';
  onContinue: () => void;
  onReviseGuided: () => void;
}) {
  const passed = summary.score >= 60;
  return (
    <div className="space-y-5">
      <div className="text-center">
        <div
          className={`mx-auto mb-3 flex h-16 w-16 items-center justify-center rounded-full ${
            passed ? 'bg-xp-50' : 'bg-amber-50'
          }`}
        >
          <Trophy className={`h-8 w-8 ${passed ? 'text-xp-500' : 'text-amber-500'}`} />
        </div>
        <p className="font-display text-ink-900 text-3xl font-extrabold">{summary.score}%</p>
        <p className="text-ink-500 mt-1 font-sans text-sm">
          {summary.correct} / {summary.total} bonnes réponses · +{summary.xpAwarded} XP
        </p>
      </div>

      {/* Erreurs à revoir */}
      {summary.wrongQuestions.length > 0 && (
        <section className="space-y-2">
          <p className="text-ink-400 text-xs font-semibold uppercase tracking-wider">
            {accessStatus === 'PREMIUM' ? 'Faiblesses détectées' : 'À revoir'}
          </p>
          {summary.wrongQuestions.map((wq) => (
            <div
              key={wq.step_id}
              className="border-ink-100 rounded-xl border bg-white px-4 py-3 text-sm"
            >
              <div className="text-ink-900 mb-1.5">
                <MarkdownMath>{wq.prompt_md}</MarkdownMath>
              </div>
              {wq.correct_label_md && (
                <p className="text-xp-700 text-xs">
                  <span className="font-semibold">Bonne réponse : </span>
                  <MarkdownMath>{wq.correct_label_md}</MarkdownMath>
                </p>
              )}
              <div className="text-ink-500 mt-1 text-xs">
                <MarkdownMath>{wq.explanation_md}</MarkdownMath>
              </div>
            </div>
          ))}
        </section>
      )}

      {accessStatus === 'PREMIUM' && summary.wrongQuestions.length > 0 && (
        <div className="bg-primary-50 border-primary-100 rounded-xl border px-4 py-3">
          <p className="text-primary-800 font-sans text-[13px]">
            Ces questions sont intégrées à ton suivi de maîtrise par compétence — ton prochain
            Warmup ciblera davantage tes points faibles.
          </p>
        </div>
      )}

      {/* Actions — primaire en premier dans le DOM */}
      <div className="flex flex-col gap-2">
        <Button onClick={onContinue} fullWidth className="flex items-center justify-center gap-2">
          <RotateCw className="h-4 w-4" /> Continuer · 10 nouvelles questions
        </Button>
        <Button
          variant="outline"
          fullWidth
          onClick={onReviseGuided}
          className="flex items-center justify-center gap-2"
        >
          <BookOpenCheck className="h-4 w-4" /> Choisir un exercice guidé
        </Button>
      </div>
    </div>
  );
}
